/**
 * Auth-gated wrapper around TanStack Query's useQuery.
 *
 * Automatically disables queries until auth initialization completes
 * (token validated or refreshed). Gates on `authReady` — NOT on
 * `isAuthenticated`, which is set during Zustand rehydration before
 * the token is validated and may be true with an expired token.
 *
 * Composes with caller-provided `enabled`: the query only runs when
 * BOTH authReady AND options.enabled are true.
 */

import { useQuery, type UseQueryOptions, type QueryKey } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/auth';

export function useAuthenticatedQuery<
  TQueryFnData = unknown,
  TError = Error,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(options: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>) {
  const authReady = useAuthStore((s) => s.authReady);

  return useQuery({
    ...options,
    enabled: authReady && (options.enabled ?? true),
  });
}
